import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  Renderer2,
} from '@angular/core';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { Observable, map, startWith } from 'rxjs';

@Component({
  selector: 'app-custom-input',
  templateUrl: './custom-input.component.html',
  styleUrl: './custom-input.component.scss',
})
export class CustomInputComponent implements OnInit {
  constructor(private renderer: Renderer2, private el: ElementRef) {}

  @Input() label: string = '';
  @Input() showLabel: boolean = true;
  @Input() placeholder = '';
  @Input() helper = '';
  @Input() currency = '';
  @Input() required: boolean = false;
  @Input() errorMessage: string = '';
  @Input() sideIcon: string = '';
  @Input() max: number;
  @Input() type: string = 'text';
  @Input() inputmode: string = '';
  /** When true, the input accepts digits and a single decimal comma only (rejects everything else). */
  @Input() decimalOnly = false;
  @Input() isDisabled = false;
  @Input() controlName: string = '';
  @Input() parentForm: FormGroup;
  @Input() arrayName: string;
  @Input() parentGroupName: any;
  @Input() inputClass: string;
  @Input() innerClass: string;
  @Input() value: any = '';
  @Input() options: any[] = [];
  @Input() filteredOptions: Observable<any[]>;
  @Input() control: FormControl;
  @Input() positionIcon = 'left';
  @Input() autocomplete: string = 'off';
  @Input() showPassword: boolean;
  @Output() handleIconClick = new EventEmitter<any>() || undefined;
  @Output() input = new EventEmitter<any>();

  ngOnInit(): void {
    if (this.parentGroupName) {
      if (typeof this.parentGroupName === 'string') {
        this.renderer.setAttribute(
          this.el.nativeElement,
          'formGroupName',
          this.parentGroupName
        );
      }
    }
    if (this.parentForm?.valueChanges) {
      this.filteredOptions = this.parentForm?.valueChanges?.pipe(
        startWith(''),
        map((value) => {
          const name =
            typeof value === 'string' ? value : value[this.controlName];
          return name ? this._filter(name as string) : this.options.slice();
        })
      );
    }
  }
  displayFn(itemFromList: any): string {
    return itemFromList && itemFromList.value
      ? itemFromList.value
      : itemFromList.label;
  }

  private _filter(name: string): any[] {
    const filterValue = name.toLowerCase();

    return this.options.filter((option) =>
      option.value.toLowerCase().includes(filterValue)
    );
  }

  get inError(): boolean {
    if (this.parentGroupName && !this.arrayName) {
      // @ts-ignore
      return (
        this.parentForm?.get(this.parentGroupName)?.get(this.controlName)
          ?.invalid &&
        this.parentForm?.get(this.parentGroupName)?.get(this.controlName)
          ?.touched
      );
    }
    if (this.arrayName) {
      // @ts-ignore
      const arrayForm = this.parentForm.get(this.arrayName) as FormArray;
      // @ts-ignore
      return (
        arrayForm.controls[this.parentGroupName]?.get(this.controlName)
          ?.invalid &&
        arrayForm.controls[this.parentGroupName]?.get(this.controlName)?.touched
      );
    }
    // @ts-ignore
    return (
      this.parentForm?.get(this.controlName)?.invalid &&
      this.parentForm?.get(this.controlName)?.touched
    );
  }

  onInput(event: any) {
    if (this.decimalOnly) {
      this.sanitizeDecimal(event);
    }
    this.input.emit(event);
  }

  /**
   * Keeps only digits and a single decimal comma. Any letter/symbol is stripped on the spot,
   * dots are normalized to a comma, and extra separators are dropped — so the field can never
   * hold a non-numeric value.
   */
  private sanitizeDecimal(event: any): void {
    const el = event?.target as HTMLInputElement;
    if (!el) {
      return;
    }
    let v = (el.value ?? '').replace(/[^\d.,]/g, '');
    const firstSep = v.search(/[.,]/);
    if (firstSep !== -1) {
      const intPart = v.slice(0, firstSep).replace(/[.,]/g, '');
      const decPart = v.slice(firstSep + 1).replace(/[.,]/g, '');
      v = intPart + ',' + decPart;
    }
    if (v === el.value) {
      return;
    }
    el.value = v;
    const control = this.parentForm?.get(this.controlName);
    if (control) {
      control.setValue(v, { emitEvent: false });
      control.updateValueAndValidity({ emitEvent: false });
    }
  }
}
